/**
 * FullAutopilotTab — Unified weekly autopilot orchestrator.
 *
 * Master toggle ON/OFF + per-step schedule editor (day + time) + last runs timeline
 * + manual "run step now" + "start full run now" override.
 *
 * When master is OFF, the 3 individual autopilots (Brief / Newsletter / Podcast)
 * are also force-stopped. When master is ON, the individual schedulers skip their
 * own cron to avoid double-trigger (handled backend-side via is_enabled() check).
 */
import React, { useState, useEffect, useCallback } from "react";
import { TabsContent } from "../ui/tabs";
import { Switch } from "../ui/switch";
import {
  Zap, Calendar, Clock, RefreshCw, Play, AlertTriangle, CheckCircle2,
  XCircle, SkipForward, Loader2, Mail, ChevronDown, ChevronRight, Ban,
  Activity, Hourglass, RotateCcw, Mic,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../AuthContext";
import { API } from "../../config";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const STATUS_META = {
  pending:    { color: "#bac9cc", bg: "#f3f4f5", label: "Pending",    Icon: Clock },
  running:    { color: "#1d4ed8", bg: "#dbeafe", label: "Running",    Icon: Loader2 },
  success:    { color: "#166534", bg: "#dcfce7", label: "Success",    Icon: CheckCircle2 },
  failed:     { color: "#991b1b", bg: "#fee2e2", label: "Failed",     Icon: XCircle },
  skipped:    { color: "#92400e", bg: "#fef3c7", label: "Skipped",    Icon: SkipForward },
  rejected:   { color: "#991b1b", bg: "#fee2e2", label: "Rejected",   Icon: Ban },
  superseded: { color: "#6b7a7d", bg: "#e8ecee", label: "Superseded", Icon: SkipForward },
  completed:  { color: "#166534", bg: "#dcfce7", label: "Completed",  Icon: CheckCircle2 },
  halted:     { color: "#92400e", bg: "#fef3c7", label: "Halted",     Icon: Ban },
  expired:    { color: "#6b7a7d", bg: "#e8ecee", label: "Expired",    Icon: Clock },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.Icon;
  const spin = status === "running" ? "animate-spin" : "";
  return (
    <span
      data-testid={`step-status-${status}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: meta.bg, color: meta.color,
        padding: "2px 8px", borderRadius: 99,
        fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
      }}
    >
      <Icon className={spin} style={{ width: 11, height: 11 }} />
      {meta.label}
    </span>
  );
};

/* ─── LiveTimeline — vertical pipeline visualizing the current/last run ─────── */

function _fmtTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
}
function _fmtDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
}
function _stepDuration(step) {
  if (!step?.started_at || !step?.completed_at) return null;
  const secs = Math.round((new Date(step.completed_at) - new Date(step.started_at)) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

/* ─── ExternalWaitPanel — step 6 soft-rejected → waiting for MC's English script ── */

function ExternalWaitPanel({ run, dm, awaiting, onResume, onCancelWait, onReopen }) {
  const deadline = run.external_deadline ? new Date(run.external_deadline) : null;
  const msLeft = deadline ? deadline - Date.now() : null;
  const hoursLeft = msLeft !== null ? Math.max(0, Math.round(msLeft / 36e5)) : null;
  const received = !!run.external_received;
  const closedReason = run.external_wait_expired ? "wait window expired" : run.external_wait_cancelled ? "wait cancelled" : "halted by reject";
  const btn = (extra) => `inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${extra}`;
  return (
    <div
      data-testid="external-wait-panel"
      data-awaiting={awaiting ? "true" : "false"}
      className="mb-5 px-4 py-3 rounded-xl"
      style={{
        background: dm ? "rgba(245,158,11,0.08)" : "#fffbeb",
        border: `1px solid ${dm ? "rgba(245,158,11,0.25)" : "#fde68a"}`,
      }}
    >
      <div className="flex items-start gap-2">
        <Hourglass className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#b45309" }} />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold ${dm ? "text-amber-200" : "text-amber-900"}`}>
            {awaiting ? "Podcast script soft-rejected — waiting for Mission Control's English script" : `Podcast tail closed (${closedReason}) — a late external script can still reopen it`}
          </p>
          <div className={`mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] ${dm ? "text-amber-200/80" : "text-amber-800"}`}>
            {awaiting && deadline && (
              <span data-testid="external-deadline">
                Window closes <strong>{deadline.toLocaleString([], { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</strong>
                {hoursLeft !== null && <> (~{hoursLeft} h left)</>} · on timeout: <strong>{run.external_timeout_action || "close"}</strong>
              </span>
            )}
            <span data-testid="external-received">
              Script received: <strong>{received ? `yes${run.external_received_at ? ` · ${_fmtDateTime(run.external_received_at)}` : ""}` : "not yet"}</strong>
            </span>
            {awaiting && <span>Audio &amp; publish stay <strong>pending</strong> (never skipped) until the push arrives — then they run on their own.</span>}
          </div>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {awaiting ? (
              <>
                <button
                  onClick={onResume}
                  data-testid="external-resume-btn"
                  className={btn(dm ? "bg-cyan-400/15 hover:bg-cyan-400/25 text-cyan-300" : "bg-[#006874]/10 hover:bg-[#006874]/20 text-[#006874]")}
                >
                  <Play className="w-2.5 h-2.5" /> Resume from external script
                </button>
                <button
                  onClick={onCancelWait}
                  data-testid="external-cancel-wait-btn"
                  className={btn(dm ? "bg-red-400/10 hover:bg-red-400/20 text-red-300" : "bg-red-50 hover:bg-red-100 text-red-700")}
                >
                  <Ban className="w-2.5 h-2.5" /> Cancel wait · skip podcast this week
                </button>
              </>
            ) : (
              <button
                onClick={onReopen}
                data-testid="external-reopen-btn"
                className={btn(dm ? "bg-cyan-400/15 hover:bg-cyan-400/25 text-cyan-300" : "bg-[#006874]/10 hover:bg-[#006874]/20 text-[#006874]")}
              >
                <RotateCcw className="w-2.5 h-2.5" /> Reopen with external script
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LastRetryPanel({ retry, dm, options }) {
  if (!retry) return null;
  const label = (k) => (options?.steps?.find(s => s.key === k)?.label || k).replace(/^\d+\.\s*/, "");
  const tone = retry.state === "completed" ? { bg: dm ? "rgba(16,185,129,0.12)" : "#ecfdf5", fg: dm ? "#6ee7b7" : "#065f46" }
    : retry.state === "failed" ? { bg: dm ? "rgba(220,38,38,0.12)" : "#fee2e2", fg: dm ? "#fca5a5" : "#7f1d1d" }
    : { bg: dm ? "rgba(34,211,238,0.12)" : "#ecfeff", fg: dm ? "#67e8f9" : "#155e75" };
  return (
    <div data-testid="last-retry-panel" className="mb-4 px-3 py-2 rounded-xl text-[11px] flex items-start gap-2 flex-wrap" style={{ background: tone.bg, color: tone.fg }}>
      <RotateCcw className={`w-3.5 h-3.5 mt-0.5 ${retry.state === "running" ? "animate-spin" : ""}`} />
      <div className="flex-1 min-w-[200px]">
        <span className="font-bold uppercase tracking-wider text-[9px]" data-testid="last-retry-state">ultimo retry · {retry.state}</span>
        <div>
          <strong>{label(retry.step)}</strong> · da {retry.by || "—"} · {retry.started_at ? _fmtDateTime(retry.started_at) : "—"}
          {retry.finished_at && <> → {_fmtTime(retry.finished_at)}</>}
        </div>
        {(retry.downstream || []).length > 0 && (
          <div className="mt-0.5 opacity-90" data-testid="last-retry-downstream">
            poi: {retry.downstream.map(d => `${label(d.step)}: ${d.outcome.replace(/_/g, " ")}`).join(" · ")}
          </div>
        )}
        {retry.state === "running" && (retry.planned_downstream || []).length > 0 && (
          <div className="mt-0.5 opacity-80">in coda: {retry.planned_downstream.map(label).join(", ")}</div>
        )}
      </div>
    </div>
  );
}

function LiveTimeline({ run, options, schedule, dm, masterEnabled, onRunStep, onRetryStep, onResume, onCancelWait, onReopen }) {
  if (!run || !options) return null;

  const steps = options.steps.map(s => ({ ...s, ...(run.steps?.[s.key] || {}) }));
  const completed = steps.filter(s => s.status === "success").length;
  const currentRunning = steps.find(s => s.status === "running");
  const hasFailure = steps.some(s => s.status === "failed");
  const hasReject = steps.some(s => s.rejected);
  const awaiting = !!run.awaiting_external_script;
  const podcastPublished = run.steps?.publish_podcast?.status === "success";
  const podcastReopenable = !awaiting && !podcastPublished && !currentRunning && ["generate_podcast_audio", "publish_podcast"]
    .some(k => run.steps?.[k]?.status === "skipped" && ["halted_by_reject", "external_wait_expired", "external_wait_cancelled"].includes(run.steps?.[k]?.reason));
  const progressPct = Math.round((completed / steps.length) * 100);

  // Overall status indicator
  let overallStatus = "idle";
  let overallLabel = "Idle";
  let overallColor = "#bac9cc";
  if (currentRunning) {
    overallStatus = "running"; overallLabel = `Step ${steps.indexOf(currentRunning) + 1} running`;
    overallColor = dm ? "#22d3ee" : "#006874";
  } else if (awaiting) {
    overallStatus = "awaiting_external"; overallLabel = "Waiting for external script"; overallColor = "#b45309";
  } else if (hasFailure) {
    overallStatus = "failed"; overallLabel = "Halted by failure"; overallColor = "#dc2626";
  } else if (hasReject) {
    overallStatus = "rejected"; overallLabel = "Halted by reject"; overallColor = "#dc2626";
  } else if (completed === steps.length) {
    overallStatus = "completed"; overallLabel = "Completed"; overallColor = "#166534";
  } else if (completed > 0) {
    overallStatus = "in_progress"; overallLabel = `${completed} of ${steps.length} done`; overallColor = "#006874";
  } else if (masterEnabled) {
    overallStatus = "scheduled"; overallLabel = "Waiting for next step"; overallColor = "#6b7a7d";
  }

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const cardShadow = { boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" };

  return (
    <div className={card} style={cardShadow} data-testid="live-timeline" data-overall-status={overallStatus}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 mb-1">
            <Activity className={`w-4 h-4 ${currentRunning ? "animate-pulse" : ""}`} style={{ color: overallColor }} />
            <h3 className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
              Current run · Week {run.week_id}
            </h3>
            <span
              data-testid="overall-status-pill"
              style={{
                fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                color: "#fff", background: overallColor, padding: "2px 9px", borderRadius: 99,
              }}
            >{overallLabel}</span>
          </div>
          <p className={`text-[11px] ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Run id: <code style={{ background: dm ? "rgba(255,255,255,0.05)" : "#f3f4f5", padding: "1px 5px", borderRadius: 4 }}>{run.id?.slice(0, 12)}…</code>
            {run.started_at && <> · started {_fmtDateTime(run.started_at)}</>}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`text-2xl font-bold ${dm ? "text-slate-200" : "text-[#191c1d]"}`} style={{ fontVariantNumeric: "tabular-nums" }}>
            {progressPct}%
          </span>
          <span className={`text-[10px] uppercase tracking-wider font-bold ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            {completed}/{steps.length} complete
          </span>
        </div>
      </div>

      {(awaiting || podcastReopenable) && (
        <ExternalWaitPanel run={run} dm={dm} awaiting={awaiting} onResume={onResume} onCancelWait={onCancelWait} onReopen={onReopen} />
      )}
      <LastRetryPanel retry={run.last_retry} dm={dm} options={options} />

      {/* Progress bar */}
      <div className="relative w-full h-1.5 rounded-full overflow-hidden mb-6" style={{ background: dm ? "rgba(255,255,255,0.06)" : "#f3f4f5" }}>
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
          style={{
            width: `${progressPct}%`,
            background: awaiting ? "#d97706" : hasFailure || hasReject ? "#dc2626" : (dm ? "linear-gradient(90deg,#22d3ee,#06b6d4)" : "linear-gradient(90deg,#006874,#0e8e98)"),
          }}
        />
        {currentRunning && (
          <div
            className="absolute inset-y-0 rounded-full animate-pulse"
            style={{
              left: `${progressPct}%`, width: `${100/steps.length}%`,
              background: dm ? "rgba(34,211,238,0.4)" : "rgba(0,104,116,0.35)",
            }}
          />
        )}
      </div>

      {/* Vertical timeline */}
      <div className="relative">
        {/* Vertical connecting line */}
        <div
          className="absolute left-[15px] top-3 bottom-3 w-px"
          style={{ background: dm ? "rgba(255,255,255,0.08)" : "#e8ecee" }}
        />

        <div className="space-y-3">
          {steps.map((s, idx) => {
            const status = s.status || "pending";
            const meta = STATUS_META[status] || STATUS_META.pending;
            const Icon = meta.Icon;
            const isRunning = status === "running";
            const isCurrent = isRunning || (status === "pending" && (idx === 0 || steps[idx - 1]?.status === "success"));

            const schedCfg = schedule?.[s.key];
            const schedLabel = schedCfg
              ? `${DAYS[schedCfg.dow]?.slice(0, 3) || "?"} ${String(schedCfg.hour).padStart(2, "0")}:${String(schedCfg.minute).padStart(2, "0")}`
              : null;

            return (
              <div
                key={s.key}
                data-testid={`timeline-step-${s.key}`}
                data-status={status}
                className="relative flex items-start gap-3"
              >
                {/* Node circle */}
                <div
                  className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: meta.bg,
                    color: meta.color,
                    boxShadow: isCurrent
                      ? `0 0 0 4px ${dm ? "rgba(34,211,238,0.18)" : "rgba(0,104,116,0.12)"}, 0 0 0 1px ${meta.color}40`
                      : `0 0 0 1px ${dm ? "rgba(255,255,255,0.05)" : "#e8ecee"}`,
                  }}
                >
                  <Icon
                    className={isRunning ? "animate-spin" : ""}
                    style={{ width: 14, height: 14 }}
                  />
                </div>

                {/* Step body */}
                <div
                  className={`flex-1 min-w-0 pb-3 ${isCurrent ? "" : ""}`}
                  style={{
                    paddingTop: 5,
                  }}
                >
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`text-xs font-bold ${dm ? "text-slate-500" : "text-[#bac9cc]"}`} style={{ fontVariantNumeric: "tabular-nums" }}>
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}
                      style={{ opacity: status === "pending" ? 0.6 : 1 }}
                    >
                      {s.label?.replace(/^\d+\.\s*/, "")}
                    </span>
                    {s.rejectable && status === "pending" && (
                      <span className={`text-[9px] font-bold uppercase tracking-wider flex items-center gap-0.5 ${dm ? "text-amber-400" : "text-amber-600"}`}>
                        <Mail className="w-2.5 h-2.5" /> approval
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 mt-1 text-[11px] flex-wrap">
                    {/* Scheduled time */}
                    {schedLabel && (
                      <span className={dm ? "text-slate-500" : "text-[#6b7a7d]"}>
                        ⏱ {schedLabel}
                      </span>
                    )}
                    {/* Completion time */}
                    {s.completed_at && (
                      <span className={`flex items-center gap-1 ${dm ? "text-emerald-400" : "text-emerald-700"}`}>
                        ✓ {_fmtTime(s.completed_at)}
                        {_stepDuration(s) && <span className="opacity-60">({_stepDuration(s)})</span>}
                      </span>
                    )}
                    {/* Running marker */}
                    {isRunning && (
                      <span className={`flex items-center gap-1 font-medium ${dm ? "text-cyan-400" : "text-[#006874]"}`}>
                        <Loader2 className="w-2.5 h-2.5 animate-spin" />
                        Started {_fmtTime(s.started_at)}
                      </span>
                    )}
                    {/* Attempt indicator */}
                    {s.attempt && s.attempt > 1 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-100 text-amber-700"}`}>
                        attempt {s.attempt}
                      </span>
                    )}
                  </div>

                  {/* Error message */}
                  {s.error && status === "failed" && (
                    <div
                      data-testid={`timeline-error-${s.key}`}
                      className="mt-2 px-3 py-2 rounded-lg text-[11px] font-mono leading-relaxed"
                      style={{
                        background: dm ? "rgba(220,38,38,0.1)" : "#fee2e2",
                        color: dm ? "#fca5a5" : "#7f1d1d",
                        border: `1px solid ${dm ? "rgba(220,38,38,0.2)" : "#fecaca"}`,
                      }}
                    >
                      {String(s.error).slice(0, 240)}{String(s.error).length > 240 ? "…" : ""}
                    </div>
                  )}

                  {/* Rejected note */}
                  {s.rejected && (
                    <div
                      data-testid={`timeline-rejected-${s.key}`}
                      className="mt-2 px-3 py-1.5 rounded-lg text-[11px]"
                      style={{
                        background: dm ? "rgba(220,38,38,0.08)" : "#fff5f5",
                        color: dm ? "#fca5a5" : "#7f1d1d",
                      }}
                    >
                      {s.key === "generate_podcast_script" && awaiting
                        ? "Soft-rejected — waiting for Mission Control's English script (audio & publish on hold, not skipped)."
                        : `Rejected via ${s.decided_via === "api" ? "Mission Control" : "email"} — downstream halted.`}
                    </div>
                  )}

                  {/* On-hold note for the podcast tail during the external wait */}
                  {awaiting && status === "pending" && ["generate_podcast_audio", "publish_podcast"].includes(s.key) && (
                    <div
                      data-testid={`timeline-onhold-${s.key}`}
                      className={`mt-1 text-[10px] font-bold uppercase tracking-wider ${dm ? "text-amber-400" : "text-amber-700"}`}
                    >
                      on hold · awaiting external script
                    </div>
                  )}
                  {s.key === "publish_podcast" && status === "success" && s.result?.publish_mode === "scheduled" && s.result?.scheduled_at && (
                    <div className={`mt-1 text-[10px] font-bold uppercase tracking-wider ${dm ? "text-sky-400" : "text-sky-700"}`} data-testid="timeline-publish-scheduled">
                      uploaded · goes live {new Date(s.result.scheduled_at).toLocaleString("en-GB", { timeZone: "Europe/Rome", weekday: "short", hour: "2-digit", minute: "2-digit" })} Rome (ITA slot)
                    </div>
                  )}
                  {status === "skipped" && s.reason && (
                    <div className={`mt-1 text-[10px] ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`} data-testid={`timeline-skip-reason-${s.key}`}>
                      skipped: {String(s.reason).replace(/_/g, " ")}
                    </div>
                  )}

                  {/* Action buttons */}
                  {status === "failed" && (
                    <button
                      onClick={() => onRetryStep(s.key)}
                      data-testid={`timeline-retry-${s.key}`}
                      title="Riprova con gli stessi output a monte e prosegui subito con gli step successivi"
                      className={`mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${dm ? "bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400" : "bg-[#006874]/10 hover:bg-[#006874]/20 text-[#006874]"}`}
                    >
                      <RotateCcw className="w-2.5 h-2.5" /> Riprova step
                    </button>
                  )}
                  {status === "skipped" && (
                    <button
                      onClick={() => onRunStep(s.key)}
                      data-testid={`timeline-rerun-${s.key}`}
                      className={`mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${dm ? "bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400" : "bg-[#006874]/10 hover:bg-[#006874]/20 text-[#006874]"}`}
                    >
                      <Play className="w-2.5 h-2.5" /> Run step
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer summary */}
      {currentRunning && (
        <div className="mt-4 pt-4 border-t flex items-center gap-2 text-xs" style={{ borderColor: dm ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }}>
          <Activity className={`w-3.5 h-3.5 animate-pulse ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
          <span className={dm ? "text-slate-400" : "text-[#3b494c]"}>
            Auto-refreshing every 15s while a step is running
          </span>
        </div>
      )}
    </div>
  );
}

export default function FullAutopilotTab({ dm }) {
  const [cfg, setCfg] = useState(null);
  const [options, setOptions] = useState(null);
  const [runs, setRuns] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [startingRun, setStartingRun] = useState(false);

  const { authHeader } = useAuth();
  const headers = authHeader();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o, r] = await Promise.all([
        axios.get(`${API}/admin/full-autopilot/config`, { headers }),
        axios.get(`${API}/admin/full-autopilot/options`, { headers }),
        axios.get(`${API}/admin/full-autopilot/runs?limit=10`, { headers }),
      ]);
      setCfg(c.data);
      setOptions(o.data);
      setRuns(r.data.runs || []);
    } catch (e) {
      toast.error("Failed to load Full Autopilot config");
    } finally {
      setLoading(false);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh just the runs list (used by the live polling loop, avoids loading-state flicker)
  const refreshRunsOnly = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/full-autopilot/runs?limit=10`, { headers });
      setRuns(data.runs || []);
    } catch { /* silent */ }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh while a step is actively running — 15s polling, lighter than the
  // initial full load. Idle when no step is in `running` state.
  useEffect(() => {
    const latestRun = runs[0];
    if (!latestRun) return;
    const isActive = Object.values(latestRun.steps || {}).some(s => s?.status === "running");
    if (!isActive) return;
    const t = setInterval(refreshRunsOnly, 15000);
    return () => clearInterval(t);
  }, [runs, refreshRunsOnly]);

  const saveCfg = async (next) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/full-autopilot/config`, next, { headers });
      setCfg(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleMaster = (enabled) => {
    if (!enabled && cfg.enabled) {
      if (!window.confirm("Disabling Full Autopilot will also force-stop the 3 individual autopilots (Brief, Newsletter, Podcast). You can re-enable them individually afterwards. Continue?")) return;
    }
    saveCfg({ enabled });
  };

  const updateStep = (step, field, value) => {
    const next = { ...cfg, schedule: { ...cfg.schedule, [step]: { ...cfg.schedule[step], [field]: value } } };
    setCfg(next);
  };

  const persistStep = (step) => {
    saveCfg({ schedule: { [step]: cfg.schedule[step] } });
  };

  const startFullRunNow = async () => {
    if (!window.confirm("Start a fresh autopilot run NOW for this week? (Step 1 — news selection — will trigger immediately.)")) return;
    setStartingRun(true);
    try {
      const { data } = await axios.post(`${API}/admin/full-autopilot/start-now`, {}, { headers });
      toast.success(`Run started: ${data.run_id.slice(0, 8)}…`);
      setTimeout(loadAll, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start run");
    } finally {
      setStartingRun(false);
    }
  };

  const runStep = async (runId, step) => {
    if (!window.confirm(`Manually trigger "${step}" for this run?`)) return;
    try {
      await axios.post(`${API}/admin/full-autopilot/runs/${runId}/run-step/${step}`, {}, { headers });
      toast.success(`Step '${step}' queued`);
      setTimeout(loadAll, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Step trigger failed");
    }
  };

  // iter183/184: re-run a FAILED step with its upstream outputs and drive the downstream right away
  const retryStep = async (runId, step) => {
    if (!window.confirm(`Riprova lo step "${step}" (stessi output a monte) e prosegui subito con gli step successivi?`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/full-autopilot/runs/${runId}/retry-step/${step}`, {}, { headers });
      toast.success(data.code === "already_running" ? "Retry già in corso" : `Retry avviato · poi: ${(data.next_steps || []).join(", ") || "nessuno"}`);
      setTimeout(loadAll, 1500);
      setTimeout(refreshRunsOnly, 8000);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.detail || d?.code || "Retry failed"));
    }
  };

  // Podcast step 6 ↔ external script lifecycle (mirrors the MC control endpoints)
  const lifecycleCall = async (runId, action, payload, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const { data } = await axios.post(`${API}/admin/full-autopilot/runs/${runId}/${action}`, payload || {}, { headers });
      const labels = {
        resumed: "Resumed — script consumed, audio → publish running (~1-3 min)",
        reopened: "Run reopened — late script consumed, audio → publish running (~1-3 min)",
        already_resumed: "Already resuming — nothing restarted",
        already_published: "Podcast already published for this run",
        cancelled: "Wait cancelled — podcast skipped this week",
      };
      toast.success(labels[data.code] || data.code);
      setTimeout(loadAll, 1500);
      setTimeout(refreshRunsOnly, 8000);
    } catch (e) {
      const d = e.response?.data?.detail;
      const code = typeof d === "object" ? (d.reason || d.code) : null;
      const hints = {
        no_external_pushed: "No external script pushed for this week yet — Mission Control must push it first",
        no_external_script: "No external script pushed for this week yet — Mission Control must push it first",
        window_expired: "The wait window has closed — use Reopen instead",
        already_published: "Podcast already published for this run",
        run_still_running: "The run is still live — use Resume instead",
        not_awaiting: "This run is not waiting for an external script",
      };
      toast.error(hints[code] || (typeof d === "string" ? d : d?.detail) || `${action} failed`);
    }
  };
  const resumeExternal = (runId) => lifecycleCall(runId, "resume-from-external", {},
    "Consume Mission Control's pushed English script now and run audio → publish immediately?");
  const cancelExternalWait = (runId) => lifecycleCall(runId, "cancel-external-wait", { reason: "admin: skip podcast this week" },
    "Stop waiting for the external script? Audio & publish will be skipped for this week (a later push can still reopen the run).");
  const reopenRun = (runId) => lifecycleCall(runId, "reopen", { from_step: "generate_podcast_script" },
    "Reopen this run from step 6, consume the late external script and run audio → publish now?");

  if (loading || !cfg || !options) {
    return (
      <TabsContent value="full-autopilot">
        <div className="flex justify-center py-16">
          <RefreshCw className={`w-5 h-5 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
        </div>
      </TabsContent>
    );
  }

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const cardShadow = { boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" };
  const sub = `category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`;
  const inp = `px-2 py-1.5 rounded-lg text-sm border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`;

  return (
    <TabsContent value="full-autopilot">
      <div className="max-w-3xl space-y-6">

        {/* Header */}
        <div>
          <h2 className={`editorial-heading text-2xl font-medium flex items-center gap-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
            <Zap className="w-5 h-5" /> Full Autopilot
          </h2>
          <p className={`category-tag mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            One weekly chain that orchestrates Brief, Newsletter and Podcast. Each step is independently editable here and stays in sync with the individual settings.
          </p>
        </div>

        {/* Live timeline (current run) — pick the most active (non-superseded) run */}
        {(() => {
          const visibleRuns = runs.filter(r => r.status !== "superseded");
          // Prefer the run with most completed/running steps; tie-break by newest started_at
          const sorted = [...visibleRuns].sort((a, b) => {
            const aProg = Object.values(a.steps || {}).filter(s => ["success","running","failed","rejected"].includes(s.status)).length;
            const bProg = Object.values(b.steps || {}).filter(s => ["success","running","failed","rejected"].includes(s.status)).length;
            if (aProg !== bProg) return bProg - aProg;
            return new Date(b.started_at || 0) - new Date(a.started_at || 0);
          });
          const focal = sorted[0];
          return focal ? (
            <LiveTimeline
              run={focal}
              options={options}
              schedule={cfg.schedule}
              dm={dm}
              masterEnabled={cfg.enabled}
              onRunStep={(step) => runStep(focal.id, step)}
              onRetryStep={(step) => retryStep(focal.id, step)}
              onResume={() => resumeExternal(focal.id)}
              onCancelWait={() => cancelExternalWait(focal.id)}
              onReopen={() => reopenRun(focal.id)}
            />
          ) : null;
        })()}

        {/* Master toggle */}
        <div className={card} style={cardShadow}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Master switch</h3>
                {cfg.enabled && (
                  <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 99, fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    Active
                  </span>
                )}
              </div>
              <p className={`text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"} leading-relaxed`}>
                When <strong>ON</strong>, the orchestrator runs the unified weekly chain. Individual autopilots are paused to avoid double-trigger.
                <br/>When <strong>OFF</strong>, individual autopilots are force-stopped — re-enable them one by one from their own tabs.
              </p>
            </div>
            <Switch
              checked={cfg.enabled}
              onCheckedChange={toggleMaster}
              disabled={saving}
              data-testid="full-autopilot-master-switch"
              className="data-[state=checked]:bg-[#006874]"
            />
          </div>

          {/* Recipient + cadence row */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={`text-[10px] font-bold uppercase tracking-wider ${sub}`}>Approval email</label>
              <input
                type="email"
                value={cfg.recipient_email || ""}
                onChange={e => setCfg({ ...cfg, recipient_email: e.target.value })}
                onBlur={() => saveCfg({ recipient_email: cfg.recipient_email })}
                data-testid="recipient-email-input"
                className={`w-full mt-1 ${inp}`}
              />
            </div>
            <div>
              <label className={`text-[10px] font-bold uppercase tracking-wider ${sub}`}>Cadence</label>
              <select
                value={cfg.cadence}
                onChange={e => saveCfg({ cadence: e.target.value })}
                data-testid="cadence-select"
                className={`w-full mt-1 ${inp}`}
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly (even ISO weeks)</option>
                <option value="monthly">Monthly (1st week)</option>
              </select>
            </div>
          </div>

          {cfg.enabled && (
            <div className="mt-5 pt-4 border-t" style={{ borderColor: dm ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }}>
              <button
                onClick={startFullRunNow}
                disabled={startingRun}
                data-testid="start-full-run-btn"
                className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
              >
                {startingRun ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Start full run now
              </button>
              <p className={`text-[11px] mt-2 ${sub}`}>
                Triggers a fresh chain immediately (skips the schedule, starts with news selection).
              </p>
            </div>
          )}
        </div>

        {/* Schedule editor */}
        <div className={card} style={cardShadow}>
          <h3 className={`text-sm font-semibold mb-1 flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            <Calendar className="w-4 h-4" /> Weekly schedule
          </h3>
          <p className={`text-xs mb-4 ${sub}`}>
            All times are <strong>Europe/Rome</strong>. Each step can be disabled individually. Soft-reject emails are sent at the checkpoints below (1 · 2 · 4 · 6).
          </p>

          <div className="space-y-2">
            {options.steps.map(s => {
              const stepCfg = cfg.schedule[s.key] || { enabled: true, dow: 0, hour: 9, minute: 0 };
              return (
                <div
                  key={s.key}
                  data-testid={`step-row-${s.key}`}
                  className={`rounded-xl p-3 flex items-center gap-3 flex-wrap ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}
                >
                  <Switch
                    checked={stepCfg.enabled}
                    onCheckedChange={v => { updateStep(s.key, "enabled", v); setTimeout(() => persistStep(s.key), 50); }}
                    data-testid={`step-enabled-${s.key}`}
                    className="data-[state=checked]:bg-[#006874] scale-90"
                  />
                  <div className="flex-1 min-w-[140px]">
                    <p className={`text-sm font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{s.label}</p>
                    {s.rejectable && (
                      <p className={`text-[10px] uppercase tracking-wider font-bold ${dm ? "text-amber-400" : "text-amber-600"} flex items-center gap-1`}>
                        <Mail className="w-2.5 h-2.5" /> sends approval email
                      </p>
                    )}
                  </div>
                  <select
                    value={stepCfg.dow}
                    onChange={e => updateStep(s.key, "dow", parseInt(e.target.value, 10))}
                    onBlur={() => persistStep(s.key)}
                    data-testid={`step-dow-${s.key}`}
                    className={inp}
                  >
                    {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                  <input
                    type="number"
                    min={0} max={23}
                    value={stepCfg.hour}
                    onChange={e => updateStep(s.key, "hour", Math.max(0, Math.min(23, parseInt(e.target.value || 0, 10))))}
                    onBlur={() => persistStep(s.key)}
                    data-testid={`step-hour-${s.key}`}
                    className={`${inp} w-16 text-center`}
                  />
                  <span className={sub}>:</span>
                  <input
                    type="number"
                    min={0} max={59}
                    step={5}
                    value={stepCfg.minute}
                    onChange={e => updateStep(s.key, "minute", Math.max(0, Math.min(59, parseInt(e.target.value || 0, 10))))}
                    onBlur={() => persistStep(s.key)}
                    data-testid={`step-minute-${s.key}`}
                    className={`${inp} w-16 text-center`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* External podcast script (step 6 soft-reject → wait window) */}
        <div className={card} style={cardShadow} data-testid="external-script-config-card">
          <h3 className={`text-sm font-semibold mb-1 flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            <Mic className="w-4 h-4" /> External podcast script (Mission Control)
          </h3>
          <p className={`text-xs mb-4 ${sub}`}>
            A soft-reject of step 6 no longer closes the week: the run waits for Mission Control&apos;s English script. Audio (7) and publish (8) stay <strong>pending</strong>; the push itself resumes them. What happens if nothing arrives:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={`text-[10px] font-bold uppercase tracking-wider ${sub}`}>Wait window (hours)</label>
              <input
                type="number"
                min={1} max={336}
                value={cfg.external_script_wait_hours ?? 48}
                onChange={e => setCfg({ ...cfg, external_script_wait_hours: Math.max(1, Math.min(336, parseInt(e.target.value || 1, 10))) })}
                onBlur={() => saveCfg({ external_script_wait_hours: cfg.external_script_wait_hours ?? 48 })}
                data-testid="external-wait-hours-input"
                className={`w-full mt-1 ${inp}`}
              />
              <p className={`text-[10px] mt-1 ${sub}`}>Default 48 h from the soft-reject.</p>
            </div>
            <div>
              <label className={`text-[10px] font-bold uppercase tracking-wider ${sub}`}>On timeout</label>
              <select
                value={cfg.external_script_timeout_action || "close"}
                onChange={e => saveCfg({ external_script_timeout_action: e.target.value })}
                data-testid="external-timeout-action-select"
                className={`w-full mt-1 ${inp}`}
              >
                <option value="close">Close cleanly — skip audio &amp; publish, notify Mission Control</option>
                <option value="fallback">Fallback — publish audio from the internal script</option>
              </select>
              <p className={`text-[10px] mt-1 ${sub}`}>
                {(cfg.external_script_timeout_action || "close") === "close"
                  ? "Recommended: the English episode must match the final Italian one. A late push still reopens the run."
                  : "The internal step-6 script goes to TTS/Spreaker (flagged used_fallback_script). Mission Control is notified."}
              </p>
            </div>
          </div>
        </div>

        {/* Last runs */}
        <div className={card} style={cardShadow}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={`text-sm font-semibold flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
              <Clock className="w-4 h-4" /> Recent runs
            </h3>
            <div className="flex items-center gap-1">
              <button
                onClick={async () => {
                  if (!window.confirm("Mark all 0-progress 'running' runs as superseded? Useful when you see duplicates.")) return;
                  try {
                    const { data } = await axios.post(`${API}/admin/full-autopilot/runs/cleanup-zombies`, {}, { headers });
                    toast.success(`Cleaned ${data.cleaned_count} zombie run(s)`);
                    loadAll();
                  } catch (e) {
                    toast.error("Cleanup failed");
                  }
                }}
                data-testid="cleanup-zombies-btn"
                title="Mark all 0-progress runs as superseded"
                className={`p-1.5 rounded-lg flex items-center gap-1 ${dm ? "hover:bg-white/10 text-slate-400" : "hover:bg-slate-100 text-[#6b7a7d]"}`}
              >
                <Ban className="w-3.5 h-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Cleanup</span>
              </button>
              <button
                onClick={loadAll}
                data-testid="refresh-runs-btn"
                className={`p-1.5 rounded-lg ${dm ? "hover:bg-white/10 text-slate-400" : "hover:bg-slate-100 text-[#6b7a7d]"}`}
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {runs.length === 0 ? (
            <p className={`text-sm text-center py-6 ${sub}`}>No runs yet. Toggle the master switch ON or click &quot;Start full run now&quot;.</p>
          ) : (
            <div className="space-y-2">
              {runs.map(r => {
                const isOpen = expanded === r.id;
                const stepsArr = options.steps.map(s => ({ key: s.key, label: s.label, ...(r.steps?.[s.key] || {}) }));
                const hasError = stepsArr.some(s => s.status === "failed");
                return (
                  <div key={r.id} className={`rounded-xl ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}>
                    <button
                      onClick={() => setExpanded(isOpen ? null : r.id)}
                      data-testid={`run-row-${r.id}`}
                      className="w-full flex items-center gap-3 p-3 text-left"
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                          Week {r.week_id} {hasError && <AlertTriangle className="inline w-3 h-3 text-red-500 ml-1" />}
                          {r.last_retry && (
                            <span
                              data-testid={`run-retry-badge-${r.id}`}
                              title={`${r.last_retry.step} · ${r.last_retry.by || ""}`}
                              className="ml-2 inline-flex items-center gap-1 align-middle"
                              style={{ background: r.last_retry.state === "completed" ? "#dcfce7" : r.last_retry.state === "failed" ? "#fee2e2" : "#e0f2fe",
                                       color: r.last_retry.state === "completed" ? "#166534" : r.last_retry.state === "failed" ? "#7f1d1d" : "#075985",
                                       padding: "1px 7px", borderRadius: 99, fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}
                            >
                              <RotateCcw style={{ width: 9, height: 9 }} /> retry {r.last_retry.state}
                            </span>
                          )}
                          {r.awaiting_external_script && (
                            <span
                              data-testid={`run-awaiting-badge-${r.id}`}
                              className="ml-2 inline-flex items-center gap-1 align-middle"
                              style={{ background: "#fef3c7", color: "#92400e", padding: "1px 7px", borderRadius: 99, fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}
                            >
                              <Hourglass style={{ width: 9, height: 9 }} /> awaiting script
                            </span>
                          )}
                        </p>
                        <p className={`text-[11px] ${sub}`}>
                          {r.started_at ? new Date(r.started_at).toLocaleString() : "—"} · {stepsArr.filter(s => s.status === "success").length}/{stepsArr.length} steps complete
                          {r.awaiting_external_script && r.external_deadline && <> · window closes {new Date(r.external_deadline).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}</>}
                        </p>
                      </div>
                      <StatusPill status={r.status || "pending"} />
                    </button>

                    {isOpen && (
                      <div className="px-3 pb-3 space-y-1.5">
                        {stepsArr.map(s => (
                          <div key={s.key} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-lg" style={{ background: dm ? "rgba(255,255,255,0.02)" : "#fff" }}>
                            <span className={`flex-1 ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>{s.label}</span>
                            {s.error && (
                              <span title={s.error} className="text-red-500 text-[10px] truncate max-w-[180px]">
                                {s.error.slice(0, 60)}
                              </span>
                            )}
                            <StatusPill status={s.status || "pending"} />
                            {s.status === "failed" && (
                              <button
                                onClick={() => retryStep(r.id, s.key)}
                                title="Riprova step (stessi output a monte, prosegue subito)"
                                data-testid={`retry-step-${r.id}-${s.key}`}
                                className={`p-1 rounded-md ${dm ? "hover:bg-cyan-400/20 text-cyan-400" : "hover:bg-[#006874]/15 text-[#006874]"}`}
                              >
                                <RotateCcw className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              onClick={() => runStep(r.id, s.key)}
                              title="Re-run this step"
                              data-testid={`rerun-step-${r.id}-${s.key}`}
                              className={`p-1 rounded-md ${dm ? "hover:bg-white/10 text-slate-400" : "hover:bg-slate-200 text-[#6b7a7d]"}`}
                            >
                              <Play className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        {r.errors && r.errors.length > 0 && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-[11px] text-red-700">
                            <strong>Errors log:</strong>
                            <ul className="mt-1 list-disc pl-4">
                              {r.errors.map((e, i) => (
                                <li key={i}>{e.step}: {e.error?.slice?.(0, 120) || JSON.stringify(e.error)}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {(() => {
                          const published = r.steps?.publish_podcast?.status === "success";
                          const tailSkipped = ["generate_podcast_audio", "publish_podcast"].some(k =>
                            r.steps?.[k]?.status === "skipped" && ["halted_by_reject", "external_wait_expired", "external_wait_cancelled"].includes(r.steps?.[k]?.reason));
                          if (r.awaiting_external_script) {
                            return (
                              <div className="mt-2 flex flex-wrap gap-2">
                                <button onClick={() => resumeExternal(r.id)} data-testid={`run-resume-external-${r.id}`}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${dm ? "bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400" : "bg-[#006874]/10 hover:bg-[#006874]/20 text-[#006874]"}`}>
                                  <Play className="w-2.5 h-2.5" /> Resume from external script
                                </button>
                                <button onClick={() => cancelExternalWait(r.id)} data-testid={`run-cancel-wait-${r.id}`}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${dm ? "bg-red-400/10 hover:bg-red-400/20 text-red-300" : "bg-red-50 hover:bg-red-100 text-red-700"}`}>
                                  <Ban className="w-2.5 h-2.5" /> Cancel wait
                                </button>
                              </div>
                            );
                          }
                          if (!published && tailSkipped) {
                            return (
                              <button onClick={() => reopenRun(r.id)} data-testid={`run-reopen-${r.id}`}
                                className={`mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${dm ? "bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400" : "bg-[#006874]/10 hover:bg-[#006874]/20 text-[#006874]"}`}>
                                <RotateCcw className="w-2.5 h-2.5" /> Reopen with external script
                              </button>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Help */}
        <div className={`rounded-xl p-4 text-xs ${dm ? "bg-[#1a2122]/50 border border-white/5 text-slate-500" : "bg-[#f3f4f5]/60 border border-black/5 text-[#6b7a7d]"}`}>
          <p className="mb-1.5"><strong>How rejection works:</strong> approval emails at steps 1, 2, 4 contain a &quot;Reject&quot; link. Clicking it halts <em>all</em> downstream steps for that week (safe default).</p>
          <p className="mb-1.5"><strong>Podcast script (step 6):</strong> a soft-reject means &quot;waiting for Mission Control&apos;s English script&quot; — audio &amp; publish stay pending for the wait window and run on their own as soon as the script is pushed. On timeout the podcast tail closes cleanly (or falls back, if configured); a late push still reopens it.</p>
          <p className="mb-1.5"><strong>Failure handling:</strong> each step retries 3 times with 5-min backoff. If it still fails, the chain pauses and you receive an urgent email.</p>
          <p><strong>Dedup:</strong> articles used in any channel within the last 30 days are excluded from selection.</p>
        </div>

      </div>
    </TabsContent>
  );
}
